import type { AiAgentToolCall, AiAgentToolResult, AiChatMessage } from '../../../../data/ServerApiClient'
import { formatCurrencyCompact, formatSignedPct, trimStr } from '../cfoFormat'
import type { CfoAnalysisChartSpec } from './types'

// Staged "deterministic evidence first, then narration, then next steps" builder
// for the guided-analysis stream. As the agent's local tools return
// (`get_opex_breakdown` -> `get_gl_anomaly_detail`, `explain_metric_change`),
// this turns each tool result into inline progress cards (a short read + a
// clickable chart/table + next-step actions) BEFORE the model's final answer.
//
// Lifted verbatim from CfoHomeBrief so the Home Brief and the shared analysis
// modal render the exact same guided experience from one implementation.

type CfoAnalysisAction = { label: string; prompt: string }

const numberOrNull = (value: unknown): number | null => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const formatSignedCurrency = (value: number): string =>
  `${value >= 0 ? '+' : '-'}${formatCurrencyCompact(Math.abs(value))}`

const comparisonShortLabel = (kind: string): string => {
  switch (kind) {
    case 'qoq':
      return 'QoQ'
    case 'yoy':
      return 'YoY'
    case 'ytd':
      return 'YTD'
    case 'l3m_vs_prior3m':
      return 'Last 3mo'
    case 'l12m_vs_prior12m':
      return 'Last 12mo'
    case 'mom':
    default:
      return 'MoM'
  }
}

const cleanDriverRows = (value: unknown): Array<Record<string, any>> =>
  Array.isArray(value)
    ? value
        .filter((row): row is Record<string, any> => row != null && typeof row === 'object')
        .filter((row) => numberOrNull(row.profit_effect) != null)
    : []

/**
 * Build the inline progress cards for one tool result. `stages` dedupes cards
 * across a single agent turn (so a re-run tool does not double-render). Returns
 * `[]` when the tool result has no card to show.
 */
export const buildCfoAnalysisProgressMessages = (
  result: AiAgentToolResult,
  stages: Set<string>,
  context?: { call?: AiAgentToolCall },
): AiChatMessage[] => {
  if (!result.ok || !result.result || typeof result.result !== 'object') return []
  const payload = result.result as Record<string, any>
  const chartSpecs = Array.isArray(payload.chart_specs)
    ? payload.chart_specs.filter((chart): chart is CfoAnalysisChartSpec => chart != null && typeof chart === 'object')
    : []

  if (
    result.name === 'run_duckdb_sql' &&
    trimStr(payload.query_purpose) === 'analysis' &&
    !stages.has(`sql_analysis_${result.tool_call_id}`)
  ) {
    stages.add(`sql_analysis_${result.tool_call_id}`)
    const headers = Array.isArray(payload.headers)
      ? payload.headers.map((header) => trimStr(header)).filter(Boolean)
      : []
    const previewRows: unknown[] = Array.isArray(payload.preview_rows)
      ? payload.preview_rows
      : Array.isArray(payload.preview?.rows)
        ? payload.preview.rows
        : []
    const rows: Array<Record<string, any>> = previewRows
      .filter((row): row is any[] | Record<string, any> => Array.isArray(row) || (row != null && typeof row === 'object'))
      .slice(0, 12)
      .map((row): Record<string, any> => {
        if (!Array.isArray(row)) return row
        const out: Record<string, any> = {}
        headers.forEach((header, index) => {
          out[header || `col_${index + 1}`] = row[index]
        })
        return out
      })
    if (!headers.length || !rows.length) return []
    return [
      {
        role: 'assistant',
        content: trimStr(payload.analysis_intent) || 'Ad hoc manifest-view SQL evidence for the selected chart item.',
        metadata: {
          kind: 'progress_chart',
          fallback_sql: true,
          chart: {
            kind: rows.length <= 8 ? 'bar' : 'table',
            title: 'Ad hoc manifest-view SQL evidence',
            subtitle: trimStr(payload.summary) || null,
            columns: headers.map((header) => ({
              key: header,
              label: header.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()),
              align: /amount|value|delta|effect|pct|percent|count/i.test(header) ? 'right' : undefined,
            })),
            rows,
            items: rows
              .map((row) => {
                const label = trimStr(row.label ?? row.category_label ?? row.account_label ?? row.metric_label ?? row.metric_id)
                const value = numberOrNull(row.profit_effect ?? row.net_income_effect ?? row.delta_abs ?? row.amount ?? row.current_value)
                if (!label || value == null) return null
                return {
                  label,
                  value,
                  direction: value > 0 ? 'up' : value < 0 ? 'down' : 'neutral',
                  note: trimStr(row.source_table_names) || null,
                }
              })
              .filter((item): item is { label: string; value: number; direction: string; note: string | null } => item != null),
          },
        },
      },
    ]
  }

  if (result.name === 'run_drilldown_recipe' && !stages.has(`recipe_${trimStr(payload.recipe_id) || result.tool_call_id}`)) {
    stages.add(`recipe_${trimStr(payload.recipe_id) || result.tool_call_id}`)
    if (payload.status !== 'ready') {
      return [
        {
          role: 'assistant',
          content:
            trimStr(payload.summary) ||
            'This drilldown recipe is not ready because the required local CFO artifacts are still preparing.',
          metadata: {
            kind: 'progress_text',
            status: 'not_ready',
            recipe_id: trimStr(payload.recipe_id) || null,
            drilldown_manifest_version: trimStr(payload.manifest_version) || null,
            artifact_readiness: payload.freshness?.artifact_readiness || null,
          },
        },
      ]
    }
    const recipeLabel = trimStr(payload.recipe_label) || 'CFO drilldown'
    const metricLabel = trimStr(payload.metric_label) || trimStr(payload.metric_id) || 'the selected metric'
    const comparisonLabel = trimStr(payload.comparison_label) || comparisonShortLabel(trimStr(payload.comparison))
    const delta = numberOrNull(payload.delta_abs)
    const niEffect = numberOrNull(payload.net_income_effect)
    const actions = Array.isArray(payload.actions) ? payload.actions : []
    const messages: AiChatMessage[] = [
      {
        role: 'assistant',
        content:
          trimStr(payload.summary) ||
          `${recipeLabel}: ${metricLabel} ${comparisonLabel}${delta == null ? '' : ` moved ${formatSignedCurrency(delta)}`}${niEffect == null ? '' : `, net-income effect ${formatSignedCurrency(niEffect)}`}.`,
        metadata: {
          kind: 'progress_text',
          actions,
          recipe_id: trimStr(payload.recipe_id) || null,
          drilldown_manifest_version: trimStr(payload.manifest_version) || null,
          telemetry: payload.telemetry || null,
        },
      },
    ]
    for (const chart of chartSpecs.slice(0, 2)) {
      const denseRows = Array.isArray(chart.rows) ? chart.rows.length : 0
      messages.push({
        role: 'assistant',
        content: chart.title ? `${chart.title}.` : '',
        metadata: {
          kind: 'progress_chart',
          recipe_id: trimStr(payload.recipe_id) || null,
          drilldown_manifest_version: trimStr(payload.manifest_version) || null,
          chart: {
            ...chart,
            kind: denseRows > 12 ? 'table' : chart.kind,
          },
        },
      })
    }
    return messages
  }

  if (Array.from(stages).some((stage) => stage.startsWith('recipe_'))) {
    switch (result.name) {
      case 'run_duckdb_sql':
      case 'get_opex_breakdown':
      case 'get_gl_anomaly_detail':
      case 'compare_periods':
      case 'get_metric_trend':
      case 'explain_metric_change':
      case 'get_statement_detail':
        return []
      default:
        break
    }
  }

  if (result.name === 'get_opex_breakdown' && !stages.has('opex_breakdown')) {
    stages.add('opex_breakdown')
    const drillDepth = trimStr(context?.call?.arguments?.drill_depth ?? context?.call?.arguments?.drillDepth)
    if (payload.status !== 'ready') {
      return [
        {
          role: 'assistant',
          content:
            trimStr(payload.summary) ||
            'Opex drilldown artifacts are still downloading. I’ll use the statement-level evidence until the local tables are ready.',
          metadata: { kind: 'progress_text', status: 'not_ready' },
        },
      ]
    }
    const standoutCategories = Array.isArray(payload.standout_categories) ? payload.standout_categories : []
    const standoutAccounts = Array.isArray(payload.standout_accounts) ? payload.standout_accounts : []
    const topCategory = standoutCategories[0]
    const topAccount = standoutAccounts[0]
    const categoryDelta = numberOrNull(topCategory?.delta_abs)
    const accountDelta = numberOrNull(topAccount?.delta_abs)
    const categoryLabel = trimStr(topCategory?.category_label) || 'the top Opex category'
    const accountLabel = trimStr(topAccount?.account_label) || 'the top Opex account'
    const secondCategory = standoutCategories[1]
    const secondCategoryLabel = trimStr(secondCategory?.category_label)
    const secondCategoryDelta = numberOrNull(secondCategory?.delta_abs)
    const actions: CfoAnalysisAction[] = []
    if (topCategory) {
      actions.push({
        label: `Show ${categoryLabel} accounts`,
        prompt: `Which accounts drove ${categoryLabel} operating expense movement this month?`,
      })
    }
    if (drillDepth === 'account' && topAccount) {
      actions.push({
        label: `Check ${accountLabel} transactions`,
        prompt: `Show transaction-level GL detail and anomalies for ${accountLabel}.`,
      })
    }
    const messages: AiChatMessage[] = [
      {
        role: 'assistant',
        content: [
          'Why OpEx changed: ',
          topCategory
            ? `${categoryLabel} moved ${categoryDelta == null ? 'materially' : formatCurrencyCompact(Math.abs(categoryDelta))} ${categoryDelta != null && categoryDelta >= 0 ? 'higher' : 'lower'} than prior month`
            : 'no Opex category cleared the materiality threshold',
          secondCategoryLabel && secondCategoryDelta != null
            ? `; ${secondCategoryLabel} moved ${formatCurrencyCompact(Math.abs(secondCategoryDelta))} ${secondCategoryDelta >= 0 ? 'higher' : 'lower'}`
            : '',
          drillDepth === 'account' && topAccount
            ? `; inside that category, ${accountLabel}${accountDelta == null ? '' : ` (${formatCurrencyCompact(Math.abs(accountDelta))})`} stands out.`
            : '.',
        ].join(''),
        metadata: { kind: 'progress_text', actions },
      },
    ]
    if (chartSpecs[0]) {
      messages.push({
        role: 'assistant',
        content: 'Category drivers behind the OpEx change.',
        metadata: {
          kind: 'progress_chart',
          chart: {
            ...chartSpecs[0],
            items: (chartSpecs[0].items || []).map((item) => ({
              ...item,
              prompt: `Which accounts drove ${item.label} operating expense movement this month?`,
            })),
          },
        },
      })
    }
    if (chartSpecs[1] && standoutAccounts.length && drillDepth === 'account') {
      messages.push({
        role: 'assistant',
        content: 'Account layer: click an account to inspect transaction-level GL anomalies.',
        metadata: {
          kind: 'progress_chart',
          chart: {
            ...chartSpecs[1],
            items: (chartSpecs[1].items || []).map((item) => ({
              ...item,
              prompt: `Show transaction-level GL detail and anomalies for ${item.label}.`,
            })),
          },
        },
      })
    }
    return messages
  }

  if (result.name === 'get_gl_anomaly_detail' && !stages.has('gl_anomaly_detail')) {
    stages.add('gl_anomaly_detail')
    if (payload.status !== 'ready' || payload.gl_detail_ready === false) {
      return [
        {
          role: 'assistant',
          content:
            trimStr(payload.summary) ||
            'GL detail is still downloading. I can show the category and account drivers now and will add transaction detail once it is ready.',
          metadata: { kind: 'progress_text', status: 'not_ready' },
        },
      ]
    }
    const anomalies = Array.isArray(payload.anomalies) ? payload.anomalies : []
    const clusters = Array.isArray(payload.clusters) ? payload.clusters : []
    const messages: AiChatMessage[] = [
      {
        role: 'assistant',
        content:
          anomalies.length || clusters.length
            ? `Transaction drill: I found ${anomalies.length + clusters.length} GL anomaly candidate(s) behind ${trimStr(payload.account_label) || 'the standout account'}.`
            : `Transaction drill: GL detail is ready for ${trimStr(payload.account_label) || 'the standout account'}, but no transaction cleared the anomaly thresholds.`,
        metadata: { kind: 'progress_text' },
      },
    ]
    for (const chart of chartSpecs.slice(0, 2)) {
      messages.push({
        role: 'assistant',
        content: '',
        metadata: { kind: 'progress_chart', chart },
      })
    }
    return messages
  }

  // Period comparison → always render a comparison TABLE (+ a delta bar chart)
  // so a "compare X vs Y" interaction carries a visual, never bare text.
  if (result.name === 'compare_periods' && !stages.has('period_comparison')) {
    stages.add('period_comparison')
    const metrics = Array.isArray(payload.metrics) ? payload.metrics : []
    if (!metrics.length) return []
    const kinds = Array.isArray(payload.comparisons) ? payload.comparisons.map((k: any) => trimStr(k)) : []
    const kind = kinds[0] || 'mom'
    const pickComparison = (m: any) => {
      const list = Array.isArray(m?.comparisons) ? m.comparisons : []
      return list.find((c: any) => trimStr(c?.kind) === kind) || list[0] || null
    }
    const rows = metrics
      .slice(0, 8)
      .map((m: any) => {
        const c = pickComparison(m)
        return {
          metric: trimStr(m?.label) || trimStr(m?.id) || 'Metric',
          current: numberOrNull(c?.current_value),
          prior: numberOrNull(c?.prior_value),
          delta: numberOrNull(c?.delta_abs),
          delta_pct: numberOrNull(c?.delta_pct),
          kindLabel: trimStr(c?.label),
        }
      })
      .filter((r: any) => r.current != null || r.prior != null || r.delta != null)
    if (!rows.length) return []
    const kindLabel = rows.find((r: any) => r.kindLabel)?.kindLabel || 'comparison'
    const barItems = rows
      .filter((r: any) => r.delta != null)
      .map((r: any) => ({
        label: r.metric,
        value: r.delta as number,
        direction: (r.delta as number) > 0 ? 'up' : (r.delta as number) < 0 ? 'down' : 'neutral',
        note: r.delta_pct != null ? formatSignedPct(r.delta_pct) : (r.delta as number) >= 0 ? 'higher' : 'lower',
      }))
    const messages: AiChatMessage[] = [
      {
        role: 'assistant',
        content: `Here’s the ${kindLabel} across ${rows.length} metric(s).`,
        metadata: { kind: 'progress_text' },
      },
    ]
    if (barItems.length) {
      messages.push({
        role: 'assistant',
        content: '',
        metadata: {
          kind: 'progress_chart',
          chart: {
            kind: 'bar',
            title: `Change by metric · ${kindLabel}`,
            subtitle: 'Current period minus prior period.',
            items: barItems,
          },
        },
      })
    }
    messages.push({
      role: 'assistant',
      content: '',
      metadata: {
        kind: 'progress_chart',
        chart: {
          kind: 'table',
          title: `Period comparison · ${kindLabel}`,
          subtitle: 'Current vs prior, by metric.',
          columns: [
            { key: 'metric', label: 'Metric' },
            { key: 'current', label: 'Current', align: 'right' },
            { key: 'prior', label: 'Prior', align: 'right' },
            { key: 'delta', label: 'Δ', align: 'right' },
          ],
          rows: rows.map((r: any) => ({
            metric: r.metric,
            current: r.current ?? '—',
            prior: r.prior ?? '—',
            delta: r.delta ?? '—',
          })),
        },
      },
    })
    return messages
  }

  // Metric trend → render the trajectory as an inline bar so a "trend / last N
  // months" interaction always carries a visual.
  if (result.name === 'get_metric_trend' && !stages.has('metric_trend')) {
    stages.add('metric_trend')
    const metricId = trimStr(payload.metric_id)
    if (metricId === 'cost_of_sales') return []
    const labels = Array.isArray(payload.labels) ? payload.labels.map((l: any) => trimStr(l)) : []
    const trend = Array.isArray(payload.trend) ? payload.trend.map((v: any) => numberOrNull(v)) : []
    const points = labels
      .map((label: string, i: number) => ({ label, value: trend[i] }))
      .filter((p: any) => p.label && p.value != null)
    if (points.length < 2) return []
    const metricLabel = trimStr(payload.label) || 'Metric'
    return [
      {
        role: 'assistant',
        content: `${metricLabel} trajectory across ${points.length} point(s).`,
        metadata: {
          kind: 'progress_chart',
          chart: {
            kind: 'bar',
            title: `${metricLabel} · trend`,
            subtitle: 'Oldest → latest from the comparison windows.',
            items: points.map((p: any) => ({ label: p.label, value: p.value as number, direction: 'neutral', note: null })),
          },
        },
      },
    ]
  }

  const movement = payload.movement && typeof payload.movement === 'object' ? (payload.movement as Record<string, any>) : null
  const drivers = cleanDriverRows(payload.drivers)
  const metricId = trimStr(payload.metric_id)
  const metricLabel = trimStr(payload.label) || 'Profit'

  if (
    result.name === 'explain_metric_change' &&
    metricId &&
    metricId !== 'net_income' &&
    !metricLabel.toLowerCase().includes('profit') &&
    !stages.has(`metric_change_${metricId}`)
  ) {
    stages.add(`metric_change_${metricId}`)
    const deltaAbs = numberOrNull(movement?.delta_abs)
    const deltaPct = numberOrNull(movement?.delta_pct)
    const currentValue = numberOrNull(movement?.current_value ?? payload.latest_value)
    const priorValue = numberOrNull(movement?.prior_value)
    const selectedNetIncomeEffect = numberOrNull(payload.selected_net_income_effect)
    const comparisonKind = trimStr(payload.comparison || movement?.kind || 'mom')
    const comparisonLabel = trimStr(movement?.label) || comparisonShortLabel(comparisonKind)
    const shortComparison = comparisonShortLabel(comparisonKind)
    const expenseLike = /cost|cogs|expense|opex/i.test(metricId) || /cost|expense/i.test(metricLabel)
    const profitEffect = selectedNetIncomeEffect ?? (deltaAbs == null ? null : expenseLike ? -deltaAbs : deltaAbs)
    const headline = metricId === 'cost_of_sales'
      ? 'COGS first layer'
      : `${metricLabel} first layer`
    const movementText = deltaAbs == null
      ? `${metricLabel} did not have a complete ${comparisonLabel} comparison in the local artifact.`
      : `${metricLabel} moved ${formatSignedCurrency(deltaAbs)} ${comparisonLabel}${deltaPct == null ? '' : ` (${formatSignedPct(deltaPct)})`}.`
    const effectText = profitEffect == null
      ? ''
      : expenseLike
        ? ` Because this is an expense-like metric, that is ${formatSignedCurrency(profitEffect)} to net income before other bridge effects.`
        : ` That is ${formatSignedCurrency(profitEffect)} to net income before other bridge effects.`
    const actions: CfoAnalysisAction[] = [
      {
        label: metricId === 'cost_of_sales' ? 'Inspect COGS accounts' : `Inspect ${metricLabel} detail`,
        prompt:
          metricId === 'cost_of_sales'
            ? `Show the cost-of-sales accounts behind this ${shortComparison} COGS movement, but stop before transaction-level detail.`
            : `Show the next level of detail behind this ${shortComparison} ${metricLabel} movement, but stop before transaction-level detail.`,
      },
      {
        label: 'Compare to revenue',
        prompt:
          metricId === 'cost_of_sales'
            ? `Compare this ${shortComparison} COGS movement to revenue and gross margin for the same window.`
            : `Compare ${metricLabel} to revenue for the same ${shortComparison} window.`,
      },
    ]
    return [
      {
        role: 'assistant',
        content: `${headline}: ${movementText}${effectText}`,
        metadata: { kind: 'progress_text', actions: actions.slice(0, 2) },
      },
      {
        role: 'assistant',
        content: `${comparisonLabel} for the bar you clicked.`,
        metadata: {
          kind: 'progress_chart',
          chart: {
            kind: 'table',
            title: `${metricLabel} · ${shortComparison} change`,
            subtitle: 'Pick a next step to drill further.',
            columns: [
              { key: 'item', label: 'Item' },
              { key: 'amount', label: 'Amount', align: 'right' },
            ],
            rows: [
              { item: 'Prior window', amount: priorValue ?? '—' },
              { item: 'Current window', amount: currentValue ?? '—' },
              { item: `${shortComparison} change`, amount: deltaAbs ?? '—' },
              ...(profitEffect == null ? [] : [{ item: 'Net-income effect', amount: profitEffect }]),
            ],
          },
          actions: actions.slice(0, 2),
        },
      },
    ]
  }

  if ((metricId !== 'net_income' && !metricLabel.toLowerCase().includes('profit')) || drivers.length === 0) return []

  if (result.name === 'explain_metric_change' && !stages.has('profit_bridge')) {
    stages.add('profit_bridge')
    const deltaAbs = numberOrNull(movement?.delta_abs)
    const deltaPct = numberOrNull(movement?.delta_pct)
    const currentValue = numberOrNull(movement?.current_value ?? payload.latest_value)
    const priorValue = numberOrNull(movement?.prior_value)
    const comparisonKind = trimStr(payload.comparison || movement?.kind || 'mom')
    const comparisonLabel = trimStr(movement?.label) || comparisonShortLabel(comparisonKind)
    const shortComparison = comparisonShortLabel(comparisonKind)
    const top = drivers[0]
    const topLabel = trimStr(top.label) || 'the largest driver'
    const topEffect = numberOrNull(top.profit_effect) ?? 0
    const helped = topEffect >= 0
    const driverItems = drivers.slice(0, 5).map((driver) => {
      const effect = numberOrNull(driver.profit_effect) ?? 0
      const label = trimStr(driver.label) || trimStr(driver.metric_id) || 'Driver'
      return {
        label,
        value: effect,
        direction: effect > 0 ? 'up' : effect < 0 ? 'down' : 'neutral',
        note: effect >= 0 ? 'helped profit' : 'hurt profit',
        prompt: /expense|opex|payroll|rent|suppl|subcontract|cost/i.test(label)
          ? `Show the operating expense category breakdown behind profit for the same ${shortComparison} window.`
          : `Explain the ${label} movement one level deeper.`,
      }
    })
    const chartItems: CfoAnalysisChartSpec['items'] = [
      ...(priorValue == null ? [] : [{ label: 'Prior-window profit', value: priorValue, direction: 'neutral', note: null }]),
      ...driverItems,
      ...(currentValue == null ? [] : [{ label: 'Current-window profit', value: currentValue, direction: 'neutral', note: null }]),
    ]
    return [
      {
        role: 'assistant',
        content: [
          `Quick read: ${metricLabel} ${deltaAbs != null && deltaAbs < 0 ? 'fell' : 'changed'} ${deltaAbs == null ? '' : formatCurrencyCompact(Math.abs(deltaAbs))}`,
          deltaPct == null ? '' : ` (${formatSignedPct(deltaPct)} ${comparisonLabel})`,
          `. The first driver I see is ${topLabel}, which ${helped ? 'helped' : 'hurt'} by ${formatCurrencyCompact(Math.abs(topEffect))}.`,
        ].join(''),
        metadata: { kind: 'progress_text' },
      },
      {
        role: 'assistant',
        content: '',
        metadata: {
          kind: 'progress_chart',
          chart: {
            kind: 'waterfall',
            title: `${shortComparison} profit bridge`,
            subtitle: 'Positive bars helped profit; red bars hurt profit.',
            items: chartItems,
          },
          actions: [
            {
              label: 'Show Opex categories',
              prompt: `Show the operating expense category breakdown behind profit for the same ${shortComparison} window.`,
            },
            {
              label: 'Check revenue instead',
              prompt: `Was revenue or gross margin also a material driver of profit for the same ${shortComparison} window?`,
            },
          ],
        },
      },
      {
        role: 'assistant',
        content: 'Want to dig deeper? Click a driver in the chart, or use one of the prompts below.',
        metadata: {
          kind: 'progress_text',
          actions: [
            {
              label: 'Show Opex categories',
              prompt: 'Show the operating expense category breakdown behind profit this month.',
            },
            {
              label: 'Inspect transactions',
              prompt: 'Show transaction-level GL detail and anomalies for the largest Opex account.',
            },
          ],
        },
      },
    ]
  }

  if (result.name === 'get_statement_detail' && !stages.has('profit_driver_ranking')) {
    stages.add('profit_driver_ranking')
    return []
  }

  return []
}
